import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { orange } from '@/theme/colors'
import { NumberedMappedEvent, UnmappedEvent } from '../types'
import { formatEventTime } from '../utils'

interface EventListProps {
	mapped: NumberedMappedEvent[]
	unmapped: UnmappedEvent[]
	selectedEventId: number | null
	onSelect: (eventId: number) => void
}

const Scrollable = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 0.5rem 0;
`

const SectionHeader = styled.div`
	padding: 0.75rem 1.25rem 0.5rem;
	font-size: 0.75rem;
	font-weight: 700;
	color: #868e96;
	text-transform: uppercase;
	letter-spacing: 0.04em;
`

const Row = styled.button<{ $active: boolean; $muted: boolean }>`
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
	width: 100%;
	padding: 0.75rem 1.25rem;
	background: ${props => (props.$active ? '#fff4ee' : 'transparent')};
	border: none;
	border-left: 3px solid ${props => (props.$active ? orange : 'transparent')};
	text-align: left;
	cursor: pointer;
	transition: background 0.15s ease;
	opacity: ${props => (props.$muted ? 0.55 : 1)};

	&:hover {
		background: ${props => (props.$active ? '#fff4ee' : '#f8f9fa')};
	}
`

const Badge = styled.div<{ $muted: boolean }>`
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: ${props => (props.$muted ? '#ced4da' : orange)};
	color: white;
	font-weight: 700;
	font-size: 0.85rem;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 2px;
`

const BadgeDash = styled(Badge)`
	background: #dee2e6;
	color: #868e96;
`

const RowBody = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.2rem;
`

const RowTitle = styled.div`
	font-weight: 600;
	font-size: 0.95rem;
	color: #212529;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const RowMeta = styled.div`
	display: flex;
	gap: 0.5rem;
	font-size: 0.8rem;
	color: #6c757d;
`

const RowAddress = styled.div`
	font-size: 0.8rem;
	color: #6c757d;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

const NoLocationLabel = styled.span`
	font-size: 0.75rem;
	color: #adb5bd;
	font-style: italic;
`

const EventList: React.FC<EventListProps> = ({ mapped, unmapped, selectedEventId, onSelect }) => {
	const selectedRef = useRef<HTMLButtonElement | null>(null)

	useEffect(() => {
		if (selectedRef.current) {
			selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
		}
	}, [selectedEventId])

	return (
		<Scrollable>
			{mapped.length > 0 && (
				<>
					<SectionHeader>On the map ({mapped.length})</SectionHeader>
					{mapped.map(item => {
						const isActive = selectedEventId === item.event.id
						const time = formatEventTime(item.event.startTime)
						return (
							<Row
								key={item.event.id}
								ref={isActive ? selectedRef : null}
								type="button"
								$active={isActive}
								$muted={false}
								onClick={() => onSelect(item.event.id)}
							>
								<Badge $muted={false}>{item.order}</Badge>
								<RowBody>
									<RowTitle>{item.event.name}</RowTitle>
									<RowMeta>
										<span>Day {item.dayIndex + 1}</span>
										{time && <span>· {time}</span>}
									</RowMeta>
									{item.event.location?.fullText && (
										<RowAddress>{item.event.location.fullText}</RowAddress>
									)}
								</RowBody>
							</Row>
						)
					})}
				</>
			)}
			{unmapped.length > 0 && (
				<>
					<SectionHeader>Without location ({unmapped.length})</SectionHeader>
					{unmapped.map(item => (
						<Row
							key={item.event.id}
							type="button"
							$active={false}
							$muted={true}
							onClick={() => onSelect(item.event.id)}
						>
							<BadgeDash $muted={true}>–</BadgeDash>
							<RowBody>
								<RowTitle>{item.event.name}</RowTitle>
								<RowMeta>
									<span>Day {item.dayIndex + 1}</span>
									<NoLocationLabel>no location</NoLocationLabel>
								</RowMeta>
							</RowBody>
						</Row>
					))}
				</>
			)}
		</Scrollable>
	)
}

export default EventList
